import moment from 'moment'
import { flattenArrayRecursive } from './FlattenArray'

/**
 * Helper function to parse user-provided date string and set it to YYYY-MM-DD format
 * @param self
 * @param fieldName
 * @param showErrHandler  Optional. Whether or not to show the generic error handler
 */
export function formatDateFields (self, fieldName, showErrHandler = true) {
  if (!self.form[fieldName]) return
  let errHandler = null
  if (showErrHandler) {
    errHandler = () => {
      return self.$message({
        showClose: false,
        message: 'Invalid date format',
        type: 'error'
      })
    }
  }
  let d = new DateFormatter({ dateString: self.form[fieldName], errHandler })
  const formattedDate = d.formatSingleDateString()
  if (formattedDate) {
    self.form[fieldName] = formattedDate
    if (self.updateForm && typeof self.updateForm === 'function') {
      self.updateForm(self.form)
    }
  } else {
    self.form[fieldName] = ''
  }
}

/*
 * Returns a simple date for comparison purposes, e.g. 20190101
 */
export function getSimpleDate (date = null, format = null, hoursOffset = 0) {
  if (!date) return false
  let dateObject

  // Check if this is a Date object
  if (typeof date === 'object' && typeof date.getMonth === 'function') {
    dateObject = date

  // String
  } else if (!isNaN(date) || typeof date === 'string') {
    dateObject = new Date(date)

    // Not a date
    if (typeof dateObject.getMonth !== 'function') return false

    // Adjust for timezone if the date string does not have a time
    if (dateObject.getUTCHours() === 0) dateObject.setMinutes(dateObject.getMinutes() + dateObject.getTimezoneOffset())

  // Not a date
  } else {
    return false
  }

  if (hoursOffset && !isNaN(hoursOffset) && hoursOffset > 0) dateObject.setHours(dateObject.getHours() + hoursOffset)

  let dateString = ''
  if (format === 'yyyy-mm-dd') {
    dateString += dateObject.getFullYear() + '-'
    dateString += pad(dateObject.getMonth() + 1) + '-'
    dateString += pad(dateObject.getDate())
  } else {
    dateString += dateObject.getFullYear()
    dateString += pad(dateObject.getMonth() + 1)
    dateString += pad(dateObject.getDate())
  }

  return dateString
}

// pad 1-digit months/days
function pad (n) {
  return n < 10 ? '0' + n : n
}

/*
 * Compare two strings/dates not including times so that 1/1/2019 1am = 1/1/2019 5pm
 */
export function compareDates (date1, date2) {
  if (!date1 || !date2) return false
}

export class DateFormatter {

  constructor (opts) {
    this.fields = opts.fields
    this.form = opts.form
    this.dateString = opts.dateString
    this.errHandler = opts.errHandler
  }

  /**
   * Parses a simple ISO date string and formats it to YYYY-MM-DD using moment.js
   * @param isoDateString
   * @return {string}
   */
  static formatISODateString (isoDateString) {
    return moment(isoDateString).format('YYYY-MM-DD')
  }

  /*
  * Iterates through a form object and converts a Date object contained in any date field
  * to a formatted 'MM-DD-YYYY' string
  * @param {object} headerDeltas
  * @return {object} headerDeltas
  */
  formatDateFormFields () {
    const { fields, form } = this
    let req = []
    let fieldGroups = Object.values(fields)
    fieldGroups.forEach(v => {
      if (v && v.inputFields) {
        req.push([...Object.values(v.inputFields).filter(x => form[x.name] && x.fieldType === 'date')])
      }
    })
    req = flattenArrayRecursive(req)
    req.forEach(v => {
      if (form[v.name]) {
        this.dateString = form[v.name]
        form[v.name] = this.formatSingleDateString()
      }
    })
    return form
  }

  /**
   * Parses user-provided date string into Object containing day, month, year,
   * then uses moment.js to create date object and format it into YYYY-MM-DD
   * @return {string}
   */
  formatSingleDateString () {
    let parsed = this.parseUserDateFormats()
    if (!parsed && this.dateString) {
      if (this.errHandler && typeof this.errHandler === 'function') {
        this.errHandler()
      }
      return null
    }
    let m = moment()
      .year(parsed.year || moment().year())
      .month(parsed.month - 1) // Because moment month setter accepts zero-based month numbers (0 -11)
      .date(parsed.day)
    return m.format('YYYY-MM-DD')
  }

  /**
   * Parses user provided date string into Object containing day, month, and year
   * @return {object}
   */
  parseUserDateFormats () {
    let date = this.dateString.split('/').join(',').split('-').join(',').split(',')
    if (!date) return ''
    let ret = { day: '', month: '', year: '' }
    switch (date.length) {
      case 3:
        const chkDM = (digits) => (digits && digits.length <= 2)
        const chkYr = (digits, len) => (digits && digits.length === len)
        if (chkDM(date[0]) && chkDM(date[1]) && chkYr(date[2], 4)) {          // MM/DD/YYYY
          ret.year = date[2]
          ret.day = date[1]
          ret.month = date[0]
        } else if (chkDM(date[0]) && chkDM(date[1]) && chkYr(date[2], 2)) {   // MM/DD/YY || M/D/YY
          ret.year = this.parseTwoDigitYear(date[2])
          ret.day = date[1]
          ret.month = date[0]
        } else if (chkYr(date[0], 4) && chkDM(date[1]) && chkDM(date[2])) {   // YYYY/MM/DD
          ret.year = date[0]
          ret.day = date[2]
          ret.month = date[1]
        } else {
          return null
        }
        break
      case 2:
        const defaultToFirstDay = (monthLength) => {
          if (monthLength === 1) {
            return '1'
          } else if (monthLength === 2) {
            return '01'
          }
        }
        if (date[0].length === 4) {         // YYYY/MM
          ret.year = date[0]
          ret.month = date[1]
          ret.day = defaultToFirstDay(ret.month.length)
        } else if (date[0].length <= 2) {   // MM/DD
          ret.month = date[0]
          ret.day = date[1]
        }
        break
      case 1:
        ret = this.handleUndelimitedDateString(date[0])   // YYYYMMDD || MMDDYYYY or inverse
        break
    }
    return ret
  }

  /**
   * Parses user-provided date string when string contains no delimiters (i.e. '/' or '-')
   * Arranges the date string in two possible ways, the first being one where the year
   * might be at the start (yearIsAtStart) and the second being one where the year might
   * be at the end (yearIsAtEnd), then compare yearIsAtStart and yearIsAtEnd to determine
   * which is more likely to be a valid date. If nothing matches, returns null.
   * @param date
   * @return {*}
   */
  handleUndelimitedDateString (date) {
    if (!(date.length === 8 || date.length === 6)) return null
    let yearIsAtStart, yearIsAtEnd, firstTwoDigits, lastTwoDigits
    if (date.length === 8) {
      yearIsAtStart = [   // YYYYMMDD
        date.substring(0, 4),
        date.substring(4, 6),
        date.substring(6, 8)
      ]
      yearIsAtEnd = [     // MMDDYYYY
        date.substring(0, 2),
        date.substring(2, 4),
        date.substring(4, 8)
      ]
      firstTwoDigits = parseInt(yearIsAtStart[0][0] + yearIsAtStart[0][1])
      if (firstTwoDigits > 12 && [19, 20, 21].includes(firstTwoDigits)) {
        return { day: yearIsAtStart[2], month: yearIsAtStart[1], year: yearIsAtStart[0] }
      }
      if (firstTwoDigits <= 12) {
        return { day: yearIsAtEnd[1], month: yearIsAtEnd[0], year: yearIsAtEnd[2] }
      }
    } else if (date.length === 6) {
      yearIsAtEnd = [     // MMDDYY
        date.substring(0, 2),
        date.substring(2, 4),
        date.substring(4, 6)
      ]
      firstTwoDigits = parseInt(yearIsAtEnd[0][0] + yearIsAtEnd[0][1])
      lastTwoDigits = parseInt(yearIsAtEnd[2][0] + yearIsAtEnd[2][1])
      const getCentury = (twoDigitYear) => {
        return twoDigitYear >= 50 && twoDigitYear <= 99 ? 1900 : 2000
      }
      if (firstTwoDigits <= 12) {
        return { day: yearIsAtEnd[1], month: yearIsAtEnd[0], year: getCentury(lastTwoDigits) + lastTwoDigits }
      }
    }
    return null
  }

  /**
   * Parses a two digit year, returning 20xx if year < 50, or 19xx if year > 50
   * @param year
   */
  parseTwoDigitYear (year) {
    return year >= 50 ? parseInt(`19${year}`) : parseInt(`20${year}`)
  }
}

/*
 * Returns a simple date with next business day for comparison purposes, e.g. 20240101
 */
export function getSimpleDateWithNextBusinessDay (date = null, format = null, hoursOffset = 0) {
  if (!date) return false
  let dateObject

  // Check if this is a Date object
  if (typeof date === 'object' && typeof date.getMonth === 'function') {
    dateObject = date

  // String
  } else if (!isNaN(date) || typeof date === 'string') {
    dateObject = new Date(date)

    // Not a date
    if (typeof dateObject.getMonth !== 'function') return false

    // Adjust for timezone if the date string does not have a time
    if (dateObject.getUTCHours() === 0) dateObject.setMinutes(dateObject.getMinutes() + dateObject.getTimezoneOffset())

  // Not a date
  } else {
    return false
  }

  if (hoursOffset && !isNaN(hoursOffset) && hoursOffset > 0) dateObject.setHours(dateObject.getHours() + hoursOffset)

  let day = dateObject.getDay()
  let addDays = 1
  if (day === 5) {
    addDays = 3
  } else if (day === 6) {
    addDays = 2
  }

  dateObject.setDate(dateObject.getDate() + addDays)

  let dateString = ''
  if (format === 'yyyy-mm-dd') {
    dateString += dateObject.getFullYear() + '-'
    dateString += pad(dateObject.getMonth() + 1) + '-'
    dateString += pad(dateObject.getDate())
  } else {
    dateString += dateObject.getFullYear()
    dateString += pad(dateObject.getMonth() + 1)
    dateString += pad(dateObject.getDate())
  }

  return dateString
}
